import React, { useState, useEffect } from 'react';
import { testimonials } from '../data/mock';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Button } from './ui/button';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/50">
              <Quote className="h-4 w-4 text-white" />
            </div>
            <span className="text-purple-400 font-semibold uppercase tracking-wider text-sm">
              TESTIMONIALS
            </span>
          </div>
          <h2
            className="text-4xl sm:text-5xl font-bold text-white"
            style={{ letterSpacing: '0.08em' }}
          >
            What Our Clients Say
          </h2>
        </div>

        {/* Testimonial Slider */}
        <div className="relative">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 md:p-12 shadow-xl shadow-purple-900/30 border border-purple-500/20 overflow-hidden">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Client Image */}
              <div className="flex-shrink-0">
                <div className="relative w-32 h-32 md:w-40 md:h-40">
                  <img
                    src={testimonials[currentIndex].image}
                    alt={testimonials[currentIndex].name}
                    className="w-full h-full rounded-full object-cover shadow-lg ring-4 ring-purple-500/50"
                  />
                  <div className="absolute -top-2 -right-2 w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-purple-500/50">
                    <Quote className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Testimonial Content */}
              <div className="flex-1 text-center md:text-left overflow-hidden">
                <p className="text-lg md:text-xl text-gray-300 mb-6 leading-relaxed">
                  "{testimonials[currentIndex].text}"
                </p>
                <h4 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                  {testimonials[currentIndex].name}
                </h4>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPrevious}
              className="rounded-full w-12 h-12 border-2 border-purple-500 bg-gray-800 hover:bg-purple-600 text-purple-400 hover:text-white transition-all"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>

            {/* Dots */}
            <div className="flex space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentIndex(index);
                    setIsAutoPlaying(false);
                  }}
                  className={`h-3 rounded-full transition-all ${
                    index === currentIndex ? 'bg-gradient-to-r from-purple-500 to-blue-500 w-8' : 'bg-gray-600 w-3'
                  }`}
                />
              ))}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={goToNext}
              className="rounded-full w-12 h-12 border-2 border-purple-500 bg-gray-800 hover:bg-purple-600 text-purple-400 hover:text-white transition-all"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;